import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sun, Moon, Globe, Menu, LogOut, LayoutDashboard, User, Shield, X, Terminal } from 'lucide-react';
import { useState } from 'react';

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLang } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-12 bg-background/90 backdrop-blur-md border-b border-border/40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="w-7 h-7 rounded border border-primary/30 bg-primary/10 flex items-center justify-center">
            <Terminal className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="text-sm font-semibold tracking-tight">
            dnslab<span className="text-primary">.biz</span><span className="inline-block w-[2px] h-[14px] bg-primary ms-0.5 -mb-0.5 animate-blink" />
          </span>
        </Link>

        {/* Center */}
        <div className="hidden md:flex items-center gap-0.5 text-[11px] tracking-wider">
          <a href="/#features" className="px-3 py-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="nav-features">
            features
          </a>
          <a href="/#pricing" className="px-3 py-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="nav-pricing">
            pricing
          </a>
          <a href="/#faq" className="px-3 py-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="nav-faq">
            faq
          </a>
          {user && (
            <Link to="/dashboard" className="px-3 py-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="nav-dashboard">
              dashboard
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link to="/admin" className="px-3 py-1.5 text-muted-foreground hover:text-primary transition-colors" data-testid="nav-admin">
              admin
            </Link>
          )}
        </div>

        {/* Right */}
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="icon" onClick={toggleLang} className="h-7 w-7 rounded text-[10px] text-muted-foreground hover:text-primary" data-testid="lang-toggle">
            {lang === 'en' ? 'FA' : 'EN'}
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-7 w-7 rounded text-muted-foreground hover:text-primary" data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
          </Button>

          <div className="hidden md:flex items-center gap-1.5 ms-1">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1.5 rounded h-7 text-[11px] text-muted-foreground" data-testid="user-menu-trigger">
                    <User className="h-3 w-3" />
                    {user.email.split('@')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded border-border/40 bg-popover/95 backdrop-blur-md">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 text-[11px]" data-testid="menu-dashboard">
                      <LayoutDashboard className="h-3 w-3" /> dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 text-[11px]" data-testid="menu-admin">
                        <Shield className="h-3 w-3" /> admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-[11px] text-destructive" data-testid="menu-logout">
                    <LogOut className="h-3 w-3" /> logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="rounded h-7 text-[11px] text-muted-foreground hover:text-foreground" data-testid="nav-login-btn">
                    log in
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="rounded h-7 text-[11px] tracking-wider" data-testid="nav-register-btn">
                    sign up
                  </Button>
                </Link>
              </>
            )}
          </div>

          <Button variant="ghost" size="icon" className="md:hidden h-7 w-7 rounded" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background border-b border-border/40 px-4 pb-4 pt-2 space-y-1" data-testid="mobile-menu">
          <a href="/#features" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-[11px] text-muted-foreground">features</a>
          <a href="/#pricing" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-[11px] text-muted-foreground">pricing</a>
          {user && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-[11px] text-muted-foreground" data-testid="mobile-nav-dashboard">dashboard</Link>}
          {user?.role === 'admin' && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2 text-[11px] text-muted-foreground" data-testid="mobile-nav-admin">
              <Shield className="h-3 w-3 text-primary" /> admin
            </Link>
          )}
          <div className="flex gap-2 pt-2">
            {user ? (
              <Button variant="outline" size="sm" className="w-full rounded text-[11px]" onClick={() => { logout(); setMobileOpen(false); }}>logout</Button>
            ) : (
              <>
                <Link to="/login" className="flex-1" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" size="sm" className="w-full rounded text-[11px]">log in</Button>
                </Link>
                <Link to="/register" className="flex-1" onClick={() => setMobileOpen(false)}>
                  <Button size="sm" className="w-full rounded text-[11px]">sign up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
